/* CAF UTM attribution persistence:
 * - Captures first-touch + last-touch UTM/click IDs
 * - Persists to localStorage/cookie
 * - Syncs into Shopify cart attributes so attribution survives checkout
 */
(function () {
  'use strict';

  if (window.__cafUtmAttributionInitialized) return;
  window.__cafUtmAttributionInitialized = true;

  var STORAGE_KEY = 'caf_utm_attribution_v1';
  var COOKIE_KEY = 'caf_utm_attribution_v1';
  var CART_SYNC_KEY = 'caf_utm_cart_sync_sig_v2';
  var BOT_BLOCKER_DECISION_KEY = 'bot-blocker:decision';
  var COOKIE_DAYS = 90;
  var MAX_ATTR_LENGTH = 255;
  var BOT_BLOCKER_RETRY_MS = 250;
  var BOT_BLOCKER_MAX_WAIT_MS = 1500;

  var PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id',
    'gclid',
    'fbclid',
    'msclkid',
    'ttclid',
    'twclid',
    'li_fat_id',
    'wbraid',
    'gbraid',
    'dclid',
    'gad_source',
    'gad_campaignid',
    'srsltid',
    'rdt_cid',
    'scclid',
    'epik',
    'cto_bundle',
    'cto_pld',
    'ref'
  ];

  var PARAM_TO_KEY = {
    utm_source: 'source',
    utm_medium: 'medium',
    utm_campaign: 'campaign',
    utm_term: 'term',
    utm_content: 'content',
    utm_id: 'id',
    gclid: 'gclid',
    fbclid: 'fbclid',
    msclkid: 'msclkid',
    ttclid: 'ttclid',
    twclid: 'twclid',
    li_fat_id: 'li_fat_id',
    wbraid: 'wbraid',
    gbraid: 'gbraid',
    dclid: 'dclid',
    gad_source: 'gad_source',
    gad_campaignid: 'gad_campaignid',
    srsltid: 'srsltid',
    rdt_cid: 'rdt_cid',
    scclid: 'scclid',
    epik: 'epik',
    cto_bundle: 'cto_bundle',
    cto_pld: 'cto_pld',
    ref: 'ref'
  };

  function safeJsonParse(value) {
    if (!value || typeof value !== 'string') return null;
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  function trimTo(value, maxLength) {
    if (value == null) return '';
    var text = String(value).trim();
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength);
  }

  function getCookie(name) {
    var cookieName = name + '=';
    var decoded = decodeURIComponent(document.cookie || '');
    var parts = decoded.split(';');
    for (var i = 0; i < parts.length; i += 1) {
      var item = parts[i].trim();
      if (item.indexOf(cookieName) === 0) {
        return item.substring(cookieName.length);
      }
    }
    return '';
  }

  function hasValidBotBlockerDecision(decision) {
    return (
      decision &&
      typeof decision === 'object' &&
      (!decision.expiresAt || Number(decision.expiresAt) > Date.now())
    );
  }

  function readBotBlockerDecision() {
    return safeJsonParse(readStorage(BOT_BLOCKER_DECISION_KEY));
  }

  function getBotBlockerStatus() {
    // Bot Blocker owns bot suppression; theme-owned attribution must respect its cached decision.
    if (window.__botBlockerBlocked) return 'suppress';
    if (!document.querySelector('[data-bot-blocker-app]')) return 'allow';

    var decision = readBotBlockerDecision();
    if (!hasValidBotBlockerDecision(decision)) return 'pending';

    return decision.suppressTracking ? 'suppress' : 'allow';
  }

  function runWhenTrackingAllowed(callback, waitedMs) {
    var status = getBotBlockerStatus();
    if (status === 'suppress') return;

    if (status === 'allow') {
      callback();
      return;
    }

    var nextWaitedMs = Number(waitedMs || 0) + BOT_BLOCKER_RETRY_MS;
    if (nextWaitedMs > BOT_BLOCKER_MAX_WAIT_MS) return;

    window.setTimeout(function () {
      runWhenTrackingAllowed(callback, nextWaitedMs);
    }, BOT_BLOCKER_RETRY_MS);
  }

  function setCookie(name, value, days) {
    var expires = '';
    if (typeof days === 'number') {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
  }

  function readStorage(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      // Ignore storage errors in private mode/quota limits.
    }
  }

  function parseQueryParams() {
    var result = {};
    var search = window.location.search || '';
    if (!search) return result;

    var query = new URLSearchParams(search);
    for (var i = 0; i < PARAMS.length; i += 1) {
      var rawKey = PARAMS[i];
      var value = query.get(rawKey);
      if (!value) continue;
      var normalizedKey = PARAM_TO_KEY[rawKey];
      result[normalizedKey] = trimTo(value, 500);
    }
    return result;
  }

  function hasAttributionData(values) {
    if (!values || typeof values !== 'object') return false;
    var keys = Object.keys(values);
    for (var i = 0; i < keys.length; i += 1) {
      if (values[keys[i]]) return true;
    }
    return false;
  }

  function getReferrerHost() {
    if (!document.referrer) return '';
    try {
      return new URL(document.referrer).hostname || '';
    } catch (error) {
      return '';
    }
  }

  function createTouch(params) {
    return {
      params: params,
      page_path: window.location.pathname || '/',
      page_url: window.location.origin + window.location.pathname + window.location.search,
      referrer_host: getReferrerHost(),
      captured_at: new Date().toISOString()
    };
  }

  function readAttributionPayload() {
    var localValue = readStorage(STORAGE_KEY);
    var parsedLocal = safeJsonParse(localValue);
    if (parsedLocal) return parsedLocal;

    var cookieValue = getCookie(COOKIE_KEY);
    return safeJsonParse(cookieValue);
  }

  function writeAttributionPayload(payload) {
    if (!payload) return;
    var serialized = JSON.stringify(payload);
    writeStorage(STORAGE_KEY, serialized);
    setCookie(COOKIE_KEY, serialized, COOKIE_DAYS);
  }

  var REFERRER_SOURCE_MAP = {
    'google': { source: 'google', medium: 'organic' },
    'facebook': { source: 'facebook', medium: 'organic' },
    'bing': { source: 'bing', medium: 'organic' },
    'instagram': { source: 'instagram', medium: 'organic' },
    'pinterest': { source: 'pinterest', medium: 'organic' },
    'youtube': { source: 'youtube', medium: 'organic' },
    'tiktok': { source: 'tiktok', medium: 'organic' }
  };

  function inferSourceFromReferrer() {
    var host = getReferrerHost();
    if (!host) return null;
    var lowerHost = host.toLowerCase();

    var keys = Object.keys(REFERRER_SOURCE_MAP);
    for (var i = 0; i < keys.length; i += 1) {
      if (lowerHost.indexOf(keys[i]) !== -1) {
        return { source: REFERRER_SOURCE_MAP[keys[i]].source, medium: REFERRER_SOURCE_MAP[keys[i]].medium };
      }
    }

    // External referrer that doesn't match known sources
    if (lowerHost.indexOf(window.location.hostname) === -1) {
      return { source: host, medium: 'referral' };
    }

    return null;
  }

  function updateAttributionFromUrl(currentPayload, queryParams) {
    var payload = currentPayload && typeof currentPayload === 'object' ? currentPayload : {};
    if (!payload.version) payload.version = 1;

    var hasUtm = hasAttributionData(queryParams);
    var paramsToUse = queryParams;

    // When no UTM params, try to infer source from referrer
    if (!hasUtm) {
      var inferred = inferSourceFromReferrer();
      if (inferred) {
        paramsToUse = inferred;
        hasUtm = true;
      }
    }

    if (!hasUtm) {
      if (payload.first_touch || payload.last_touch) return payload;
      return null;
    }

    var touch = createTouch(paramsToUse);
    payload.last_touch = touch;
    payload.updated_at = touch.captured_at;

    if (!payload.first_touch || !payload.first_touch.params || !hasAttributionData(payload.first_touch.params)) {
      payload.first_touch = touch;
    }

    return payload;
  }

  function toCartAttributes(payload) {
    if (!payload || typeof payload !== 'object') return {};

    var firstTouch = payload.first_touch || {};
    var lastTouch = payload.last_touch || {};
    var firstParams = firstTouch.params || {};
    var lastParams = lastTouch.params || {};

    var attributes = {
      utm_ft_source: firstParams.source || '',
      utm_ft_medium: firstParams.medium || '',
      utm_ft_campaign: firstParams.campaign || '',
      utm_ft_term: firstParams.term || '',
      utm_ft_content: firstParams.content || '',
      utm_ft_id: firstParams.id || '',
      utm_ft_gclid: firstParams.gclid || '',
      utm_ft_fbclid: firstParams.fbclid || '',
      utm_ft_msclkid: firstParams.msclkid || '',
      utm_ft_ttclid: firstParams.ttclid || '',
      utm_ft_twclid: firstParams.twclid || '',
      utm_ft_li_fat_id: firstParams.li_fat_id || '',
      utm_ft_wbraid: firstParams.wbraid || '',
      utm_ft_gbraid: firstParams.gbraid || '',
      utm_ft_dclid: firstParams.dclid || '',
      utm_ft_gad_source: firstParams.gad_source || '',
      utm_ft_gad_campaignid: firstParams.gad_campaignid || '',
      utm_ft_srsltid: firstParams.srsltid || '',
      utm_ft_rdt_cid: firstParams.rdt_cid || '',
      utm_ft_scclid: firstParams.scclid || '',
      utm_ft_epik: firstParams.epik || '',
      utm_ft_cto_bundle: firstParams.cto_bundle || '',
      utm_ft_cto_pld: firstParams.cto_pld || '',
      utm_ft_ref: firstParams.ref || '',
      utm_ft_referrer_host: firstTouch.referrer_host || '',
      utm_ft_page: firstTouch.page_path || '',
      utm_ft_at: firstTouch.captured_at || '',

      utm_lt_source: lastParams.source || '',
      utm_lt_medium: lastParams.medium || '',
      utm_lt_campaign: lastParams.campaign || '',
      utm_lt_term: lastParams.term || '',
      utm_lt_content: lastParams.content || '',
      utm_lt_id: lastParams.id || '',
      utm_lt_gclid: lastParams.gclid || '',
      utm_lt_fbclid: lastParams.fbclid || '',
      utm_lt_msclkid: lastParams.msclkid || '',
      utm_lt_ttclid: lastParams.ttclid || '',
      utm_lt_twclid: lastParams.twclid || '',
      utm_lt_li_fat_id: lastParams.li_fat_id || '',
      utm_lt_wbraid: lastParams.wbraid || '',
      utm_lt_gbraid: lastParams.gbraid || '',
      utm_lt_dclid: lastParams.dclid || '',
      utm_lt_gad_source: lastParams.gad_source || '',
      utm_lt_gad_campaignid: lastParams.gad_campaignid || '',
      utm_lt_srsltid: lastParams.srsltid || '',
      utm_lt_rdt_cid: lastParams.rdt_cid || '',
      utm_lt_scclid: lastParams.scclid || '',
      utm_lt_epik: lastParams.epik || '',
      utm_lt_cto_bundle: lastParams.cto_bundle || '',
      utm_lt_cto_pld: lastParams.cto_pld || '',
      utm_lt_ref: lastParams.ref || '',
      utm_lt_referrer_host: lastTouch.referrer_host || '',
      utm_lt_page: lastTouch.page_path || '',
      utm_lt_at: lastTouch.captured_at || ''
    };

    var keys = Object.keys(attributes);
    for (var i = 0; i < keys.length; i += 1) {
      attributes[keys[i]] = trimTo(attributes[keys[i]], MAX_ATTR_LENGTH);
    }

    return attributes;
  }

  function getShopifySessionToken() {
    return getCookie('_shopify_s') || '';
  }

  function makeSignature(attributes) {
    try {
      return JSON.stringify({
        attributes: attributes,
        shopify_session: getShopifySessionToken()
      });
    } catch (error) {
      return '';
    }
  }

  function syncCartAttributes(attributes, options) {
    if (!attributes || !Object.keys(attributes).length) return;
    if (getBotBlockerStatus() === 'suppress') return;

    var signature = makeSignature(attributes);
    if (!signature) return;

    var shouldForce = Boolean(options && options.force);
    var previousSignature = readStorage(CART_SYNC_KEY);
    if (!shouldForce && previousSignature === signature) return;

    var payload = new URLSearchParams();
    var keys = Object.keys(attributes);
    for (var i = 0; i < keys.length; i += 1) {
      var key = keys[i];
      var value = attributes[key];
      if (value == null) continue;
      // Send empty values too, so stale cart attributes are actively cleared.
      payload.append('attributes[' + key + ']', String(value));
    }

    if (!payload.toString()) return;

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: payload.toString(),
      keepalive: true
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Failed to sync cart attributes');
        writeStorage(CART_SYNC_KEY, signature);
      })
      .catch(function () {
        // Ignore network failures; hidden-input fallback still applies on form submit.
      });
  }

  function upsertHiddenInput(form, name, value) {
    if (value == null) return;
    var selector = 'input[name="' + name.replace(/"/g, '\\"') + '"]';
    var input = form.querySelector(selector);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  function injectAttributesIntoForm(form, attributes) {
    if (!form || !attributes) return;
    var keys = Object.keys(attributes);
    for (var i = 0; i < keys.length; i += 1) {
      var key = keys[i];
      var value = attributes[key];
      if (value == null) continue;
      upsertHiddenInput(form, 'attributes[' + key + ']', value);
    }
  }

  function attachSubmitInjection(attributes) {
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form || !form.getAttribute) return;
        if (getBotBlockerStatus() === 'suppress') return;
        var action = form.getAttribute('action') || '';
        if (action.indexOf('/cart') === -1 && action.indexOf('/checkout') === -1) return;
        injectAttributesIntoForm(form, attributes);
        if (form.querySelector('[data-cart-checkout-button], [name="checkout"]')) {
          syncCartAttributes(attributes, { force: true });
        }
      },
      true
    );
  }

  function publishShopifyCustomerEvent(payload) {
    if (!payload) return;
    if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') return;

    try {
      window.Shopify.analytics.publish('caf_attribution_ready', {
        caf_attribution: payload
      });
    } catch (error) {
      // Ignore analytics publish errors; cart attributes remain the checkout handoff.
    }
  }

  function pushAttributionToDataLayer(payload) {
    if (!payload) return;
    window.cafAttribution = payload;
    if (getBotBlockerStatus() === 'suppress') return;
    if (window.dataLayer && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: 'caf_attribution_ready',
        caf_attribution: payload
      });
    }
    publishShopifyCustomerEvent(payload);
  }

  var queryParams = parseQueryParams();
  var storedPayload = readAttributionPayload();
  var updatedPayload = updateAttributionFromUrl(storedPayload, queryParams);
  if (updatedPayload) {
    writeAttributionPayload(updatedPayload);
  }

  var attributes = toCartAttributes(updatedPayload);
  runWhenTrackingAllowed(function () {
    syncCartAttributes(attributes);
    pushAttributionToDataLayer(updatedPayload);
  });
  attachSubmitInjection(attributes);
})();
